import { createContext, useCallback, useContext, useEffect, useState, PropsWithChildren, useRef } from 'react';
import supabaseClient from '@/api/supabaseClient.ts';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import useLogin from '@/api/useLogin.ts';
import useSendResetPasswordLink from '@/api/useSendResetPasswordLink.ts';
import useResetPassword from '@/api/useResetPassword.ts';
import useVerifyEmail from '@/api/useVerifyEmail.ts';
import useResendVerificationCode from '@/api/useResendVerificationCode.ts';

export type UserRole = 'admin' | 'business_provider' | 'family_member';

export type CurrentUser = {
  id: string;
  email: string;
  role: UserRole | null;
  is_verified: boolean;
  first_name: string | null;
  last_name: string | null;
  gender: string | null;
  phone_number: string | null;
  city_town: string | null;
  postcode: string | null;
  created_at: string;
  updated_at: string;
};

interface AuthContextProps {
  session: Session | null;
  supabaseClient: SupabaseClient;
  logout: () => void;
  isLoading: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
  verifyEmailQuery: ReturnType<typeof useVerifyEmail>;
  resendVerificationCodeQuery: ReturnType<typeof useResendVerificationCode>;
  currentUser: CurrentUser | null;
  isLoadingUser: boolean;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isPendingAuthChangeCheck = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem('settings_unlocked');
      sessionStorage.removeItem('settings_unlocked_time');
      sessionStorage.removeItem('business-onboarding:step');
      sessionStorage.removeItem('business-onboarding:data');
    } catch (e) {
      console.error('Error clearing session state:', e);
    }
    void supabaseClient.auth.signOut();
  }, []);

  const loginQuery = useLogin();

  const sendResetPasswordLinkQuery = useSendResetPasswordLink();

  const resetPasswordQuery = useResetPassword();

  const verifyEmailQuery = useVerifyEmail();

  const resendVerificationCodeQuery = useResendVerificationCode();

  const checkData = useCallback(async (session: Session | null) => {
    if (isPendingAuthChangeCheck.current) return;

    isPendingAuthChangeCheck.current = true;

    if (!session) {
      setIsLoading(false);
      setIsLoadingUser(false);
      setSession(null);
      setCurrentUser(null);
      isPendingAuthChangeCheck.current = false;
      return;
    }

    // Fetch user data from public.users
    try {
      const { data, error } = await supabaseClient
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) {
        console.error('Error fetching current user:', error);
        setCurrentUser(null);
      } else {
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setCurrentUser(null);
    } finally {
      setIsLoadingUser(false);
    }

    setIsLoading(false);
    setSession(session);
    isPendingAuthChangeCheck.current = false;
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      checkData(session);
    });

    return () => {
      subscription.unsubscribe();
      setIsLoading(true);
      setIsLoadingUser(true);
      setSession(null);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        supabaseClient,
        logout,
        isLoading,
        loginQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
        verifyEmailQuery,
        resendVerificationCodeQuery,
        currentUser,
        isLoadingUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}
